import { useSSRHook } from "ssr-hook";
import { useUser } from "../User";
import Icon from "@mdi/react";
import { useLang } from "./Language";
import { Loading, Remove } from ".";
import Content from "./Content";
import { useState } from "react";
import { mdiCheck, mdiDrag, mdiMinus, mdiPencil, mdiPlus } from "@mdi/js";

import { ID, Item, List } from "../../types";
import Modal from "react-responsive-modal";
import { api } from "../services/api";
import { Draggable, moveItems, useDraggable, useDraggableContext } from "react-tiny-dnd";
import { DraggableContextType } from "react-tiny-dnd/lib/types";

export default function List({ id }: { id: ID }) {
    const lang = useLang();
    const user = useUser();

    const [list, error, isLoading, reload] = useSSRHook<List>(`/api/list?listId=${decodeURIComponent(id)}`);

    const [activeIndex, setActiveIndex] = useState(-1);


    const [message, setMessage] = useState("");
    const [editList, setEditList] = useState(false);
    const [editItem, setEditItem] = useState<null | Item & { index: number }>(null);


    // drag list
    const onDrop = async (dragIndex: number, overIndex: number) => {
        if (!list) return;

        const items = moveItems(list.items, dragIndex, overIndex);
        const [, error] = await api.updateList({ ...list, items });

        if (error) {
            setMessage("Úprava pořadí položek selhalo");
            return;
        }

        setMessage("Pořadí položek v listu uloženo");
        reload();
    };

    const context = useDraggableContext({ onDrop });

    async function handleSave() {
        if (!editItem) return;

        if (!editItem.contentId) {
            // new item
            const [, error] = await api.addListItem({ listId: id, cs: editItem.cs, pl: editItem.pl });
            if (error) {
                setMessage("Přidání položky selhalo");
                return;
            }
            setMessage("Položka přidána");
        } else {
            // update item
            const [, error] = await api.updateListTitle({ listId: id, index: editItem.index, cs: editItem.cs, pl: editItem.pl });
            if (error) {
                setMessage("Přidání položky selhalo");
                return;
            }
            setMessage("Položka upravena");
        }

        reload();
        setEditItem(null);
    }

    async function handleRemove(index: number) {
        if (!list) return;
        const [, error] = await api.removeListItem({ listId: id, index });
        if (error) {
            setMessage("Mazání položky selhalo");
            return;
        }

        setMessage("Položka smazána");
        reload();
    }

    return <div className="list-element">

        {user.isAdmin && <div className="admin small">
            {!editList && <button onClick={() => setEditList(true)}>
                <Icon path={mdiPencil} size={1} /> Upravit list
            </button>}
            {editList && <button onClick={() => setEditList(false)}>
                <Icon path={mdiCheck} size={1} /> Ukončit změny
            </button>}

            <Modal
                open={editItem !== null}
                onClose={() => setEditItem(null)}
                center
                styles={{ modal: { width: "500px" } }}
            >
                {message && <p className="alert">{message}</p>}
                {editItem && <>
                    <h2>Položka listu</h2>

                    <label>Český název</label>
                    <input
                        type="text"
                        placeholder="Povinná položka"
                        value={editItem.cs}
                        onChange={(e) => setEditItem({ ...editItem, cs: e.target.value })}
                    />

                    <label>Polský název</label>
                    <input
                        type="text"
                        placeholder="Pokud není vyplněno, použije se český název"
                        value={editItem.pl}
                        onChange={(e) => setEditItem({ ...editItem, pl: e.target.value })}
                    />
                    <div className="buttons">
                        <button onClick={handleSave}>Uložit změny</button>
                    </div>
                </>}
            </Modal>
        </div>}

        {isLoading && <Loading />}
        {error && <p className="alert">Obsah se nepodařilo načíst 🙁</p>}

        {list && <>
            {list.items.length === 0 && <p className="info">Tato stránka je prázdná</p>}

            {editList && <>
                {list.items.map((item, index) => <DraggableItem
                    key={index}
                    context={context}
                    item={item}
                    index={index}
                    onEdit={() => setEditItem({ ...item, index })}
                    onRemove={() => handleRemove(index)}
                />)}
                <button className="center" onClick={() => {
                    setMessage("");
                    setEditItem({ index: -1, cs: "Nová položka", pl: "", contentId: "" });
                }}>
                    <Icon path={mdiPlus} size={1} />Přidat položku do listu
                </button>
            </>}

            {!editList && list.items.map((item, index) => <div key={index} className={`list-item${activeIndex === index ? " active" : ""}`}>
                <div className="list-header" onClick={() => setActiveIndex(activeIndex === index ? -1 : index)}>
                    <h2>{item[lang] || item.cs}</h2>
                    <Icon path={activeIndex === index ? mdiMinus : mdiPlus} size={1} />
                </div>
                <div className={`list-content${activeIndex === index ? " open" : " close"}`}>
                    <div className="inner">
                        <Content id={item.contentId} />
                    </div>
                </div>
            </div>)}

        </>}
    </div>;
}

// {activeIndex === index && <Content id={item.contentId} />}

function DraggableItem(
    { context, index, item, onEdit, onRemove }: {
        context: DraggableContextType;
        index: number;
        item: Item;
        onEdit: () => void,
        onRemove: () => void;
    },
) {
    const lang = useLang();
    const { listeners, isDragging } = useDraggable(context, index);

    return (
        <Draggable
            context={context}
            index={index}
            dividerClassName="dnd-divider"
        >
            <div className="list-item" style={{ opacity: isDragging ? 0.4 : 1 }}>
                <div className="list-header">
                    <div className="dnd-button" {...listeners}>
                        <Icon path={mdiDrag} size={2} />
                    </div>
                    <h2>{item[lang] || item.cs}</h2>
                    <div className="grow" />
                    <button onClick={onEdit}>
                        <Icon path={mdiPencil} size={1} />
                    </button>
                    <Remove onRemove={onRemove} />
                </div>
            </div>
        </Draggable>
    );
}
